import React, { useEffect, useRef, useState } from 'react';
import { useTray } from './useTray';
import {
  TrayShell,
  TrayHeader,
  TrayTitle,
  TrayCloseButton,
  TrayPanel,
  TrayResizeHandle,
  TrayEdgeHandle,
  TrayEdgeButton,
  TrayEdgeLabel,
  TrayEdgeChevron,
} from './ResizableTray.style';
import { Tr } from 'zod/v4/locales';

const CLOSE_ANIMATION_MS = 200;

export interface ResizableTrayProps {
  id: string;
  side: 'left' | 'right';
  label: string;
  defaultWidth: number;
  defaultOpen?: boolean;
  minWidth?: number;
  maxWidth?: number;
  children: React.ReactNode;
}

const ResizableTray: React.FC<ResizableTrayProps> = ({
  id,
  side,
  label,
  defaultWidth,
  defaultOpen = true,
  minWidth = 200,
  maxWidth = 600,
  children,
}) => {
  const { isOpen, width, setOpen, setWidth } = useTray(id, {
    isOpen: defaultOpen,
    width: defaultWidth,
  });

  const panelId = `tray-panel-${id}`;
  const edgeHandleRef = useRef<HTMLButtonElement>(null);
  const panelRef = useRef<HTMLElement>(null);
  const trayRef = useRef<HTMLDivElement>(null);
  const closeTimerRef = useRef<ReturnType<typeof setTimeout>>();

  // Animated display width: starts at 0 on mount, transitions to target width.
  // On close: collapses to 0 then unmounts.
  const [displayWidth, setDisplayWidth] = useState(0);
  const [isClosing, setIsClosing] = useState(false);

  // Animate open: 0 → width on mount
  useEffect(() => {
    if (isOpen) {
      const raf = requestAnimationFrame(() => setDisplayWidth(width));
      return () => cancelAnimationFrame(raf);
    }
  }, [isOpen]); // eslint-disable-line react-hooks/exhaustive-deps

  // Clear close timer on unmount to prevent state updates after unmount
  useEffect(() => {
    return () => {
      clearTimeout(closeTimerRef.current);
      closeTimerRef.current = undefined;
    };
  }, []);

  // Keep display width in sync during drag (no animation needed — transition: none while dragging)
  const isDragging = useRef(false);
  useEffect(() => {
    if (isOpen && !isClosing && isDragging.current) {
      setDisplayWidth(width);
    }
  }, [width, isOpen, isClosing]);

  const dragStartX = useRef(0);
  const dragStartWidth = useRef(0);

  const effectiveMax = Math.min(maxWidth, Math.floor(window.innerWidth * 0.5));

  const clampWidth = (w: number) => Math.min(effectiveMax, Math.max(minWidth, w));

  const onPointerDown = (e: React.PointerEvent<HTMLDivElement>) => {
    isDragging.current = true;
    dragStartX.current = e.clientX;
    dragStartWidth.current = width;
    (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId);
  };

  const onPointerMove = (e: React.PointerEvent<HTMLDivElement>) => {
    if (!isDragging.current) return;
    const delta =
      side === 'left'
        ? e.clientX - dragStartX.current
        : dragStartX.current - e.clientX;
    const next = clampWidth(dragStartWidth.current + delta);
    setWidth(next);
    setDisplayWidth(next);
  };

  const onPointerUp = () => {
    isDragging.current = false;
  };

  const onHandleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.key === 'ArrowRight') {
      e.preventDefault();
      const next = clampWidth(width + (side === 'left' ? 10 : -10));
      setWidth(next);
      setDisplayWidth(next);
    } else if (e.key === 'ArrowLeft') {
      e.preventDefault();
      const next = clampWidth(width + (side === 'left' ? -10 : 10));
      setWidth(next);
      setDisplayWidth(next);
    }
  };

  const openTray = () => {
    setOpen(true);
    requestAnimationFrame(() => {
      const firstFocusable = panelRef.current?.querySelector<HTMLElement>(
        'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])',
      );
      firstFocusable?.focus();
    });
  };

  const closeTray = () => {
    setIsClosing(true);
    setDisplayWidth(0);
    clearTimeout(closeTimerRef.current);
    closeTimerRef.current = setTimeout(() => {
      setOpen(false);
      setIsClosing(false);
      requestAnimationFrame(() => edgeHandleRef.current?.focus());
    }, CLOSE_ANIMATION_MS);
  };

  if (!isOpen) {
    return (
      <TrayEdgeHandle side={side}>
        <TrayEdgeButton
          ref={edgeHandleRef}
          side={side}
          aria-expanded={false}
          aria-controls={panelId}
          aria-label={`Open ${label} tray`}
          onClick={openTray}
          type="button"
        >
          <TrayEdgeLabel>{label}</TrayEdgeLabel>
          <TrayEdgeChevron>{side === 'left' ? '›' : '‹'}</TrayEdgeChevron>
        </TrayEdgeButton>
      </TrayEdgeHandle>
    );
  }

  const leftHeaderItem = side === 'left' ? <TrayTitle>{label}</TrayTitle> : <TrayCloseButton
    aria-label={`Close ${label} tray`}
    aria-expanded={true}
    aria-controls={panelId}
    onClick={closeTray}
    type="button"
  >
    ▶
  </TrayCloseButton>;
  const rightHeaderItem = side === 'left' ? <TrayCloseButton
    aria-label={`Close ${label} tray`}
    aria-expanded={true}
    aria-controls={panelId}
    onClick={closeTray}
    type="button"
  >
    ◀
  </TrayCloseButton> : <TrayTitle>{label}</TrayTitle>;

  return (
    <TrayShell
      ref={trayRef}
      width={displayWidth}
      side={side}
      role="region"
      aria-label={`${label} tray`}
      style={{ transition: isDragging.current ? 'none' : `width ${CLOSE_ANIMATION_MS}ms ease` }}
    >
      <TrayHeader>
        {leftHeaderItem}
        {rightHeaderItem}
      </TrayHeader>
      <TrayPanel
        ref={panelRef}
        id={panelId}
        tabIndex={0}
      >
        {children}
      </TrayPanel>
      <TrayResizeHandle
        side={side}
        role="separator"
        aria-orientation="vertical"
        aria-label={`Resize ${label} tray`}
        aria-valuenow={Math.round(width)}
        aria-valuemin={minWidth}
        aria-valuemax={effectiveMax}
        tabIndex={0}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        onKeyDown={onHandleKeyDown}
      />
    </TrayShell>
  );
};

export default ResizableTray;
